import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams, clampPayloadToCurrentScope, filterItemsByCurrentScope } from './viewerAccess';
import { formatGpsCoordinates } from '../services/gpsCoordinates';
import { publishCollectionChange } from '../utils/collectionSync';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

const COLLECTION_KEY = 'facilities';

// ─── Label maps (API value → display label) ───────────────────────────────────
const FACILITY_TYPE_LABELS = {
  veterinary_clinic:        'Veterinary Clinic',
  veterinary_hospital:      'Veterinary Hospital',
  laboratory:               'Laboratory',
  diagnostic_centre:        'Diagnostic Centre',
  live_animal_market:       'Live Animal Market',
  abattoir:                 'Abattoir',
  slaughter_slab:           'Slaughter Slab',
  grazing_reserve:          'Grazing Reserve',
  watering_point:           'Watering Point',
  milk_collection_centre:   'Milk Collection Centre',
  feedmill:                 'Feed Mill',
  cold_storage_commercial:  'Cold Storage (Commercial)',
  cold_storage_biological:  'Cold Storage (Biological)',
  breeding_farm:            'Breeding Farm',
  ai_centre:                'AI Centre',
  quarantine_station:       'Quarantine Station',
  // v2.2 climate — livestock farms and biogas digesters are registered as
  // facilities so climate data is held on the facility, not on people.
  livestock_farm:           'Livestock Farm',
  biogas_system:            'Biogas System',
  other:                    'Other',
};

const FUNCTIONAL_STATUS_LABELS = {
  active:               'Active',
  inactive:             'Inactive',
  under_maintenance:    'Under Maintenance',
  under_rehabilitation: 'Under Rehabilitation',
  decommissioned:       'Decommissioned',
};

const OWNERSHIP_LABELS = {
  federal:     'Federal',
  state:       'State',
  lga:         'LGA',
  private:     'Private',
  community:   'Community',
  ngo_partner: 'NGO Partner',
  ppp:         'PPP',
  religious:   'Religious',
};

const PRIORITY_LABELS = {
  high:   'High',
  medium: 'Medium',
  low:    'Low',
  none:   'None',
};

const LICENSED_STATUS_LABELS = {
  licensed:   'Licensed',
  unlicensed: 'Unlicensed',
  pending:    'Pending',
  exempt:     'Exempt',
};

function toLabel(map, key) {
  if (!key) return null;
  const scalar = (typeof key === 'object' && key !== null) ? (key.slug ?? key.code ?? key.name ?? null) : key;
  if (!scalar) return null;
  return (map ? map[scalar] : null) ?? String(scalar).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeComparableText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function makeValueMatcher(map = {}, aliases = {}) {
  return (value) => {
    const raw = toScalar(value, ['slug', 'code', 'value', 'name', 'label']);
    if (!raw) return null;
    const normalizedRaw = normalizeComparableText(raw);
    const direct = Object.keys(map).find((key) => normalizeComparableText(key) === normalizedRaw);
    if (direct) return direct;
    const alias = Object.entries(aliases).find(([key]) => normalizeComparableText(key) === normalizedRaw);
    if (alias) return alias[1];
    const labelMatch = Object.entries(map).find(([, label]) => normalizeComparableText(label) === normalizedRaw);
    return labelMatch ? labelMatch[0] : String(raw).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  };
}

const normalizeFacilityTypeValue = makeValueMatcher(FACILITY_TYPE_LABELS, {
  feed_mill: 'feedmill',
  'feed mill': 'feedmill',
});
const normalizeFunctionalStatusValue = makeValueMatcher(FUNCTIONAL_STATUS_LABELS, {
  // Legacy backend vocabulary — kept so existing records saved before the
  // v2.0 field-dictionary ENUM fix still display and re-save correctly.
  fully_functional: 'active',
  'fully functional': 'active',
  functional: 'active',
  partially_functional: 'under_maintenance',
  'partially functional': 'under_maintenance',
  non_functional: 'inactive',
  'non functional': 'inactive',
  'non-functional': 'inactive',
  under_construction: 'under_rehabilitation',
  'under construction': 'under_rehabilitation',
});

// The backend FunctionalStatus enum still only accepts the legacy vocabulary
// (fully_functional, partially_functional, non_functional, under_construction,
// decommissioned) and 422s on the v2 values — translate on the way out.
const FUNCTIONAL_STATUS_TO_BACKEND = {
  active:               'fully_functional',
  under_maintenance:    'partially_functional',
  inactive:             'non_functional',
  under_rehabilitation: 'under_construction',
  decommissioned:       'decommissioned',
};
const toBackendFunctionalStatus = (value) => {
  const normalized = normalizeFunctionalStatusValue(value);
  return normalized ? FUNCTIONAL_STATUS_TO_BACKEND[normalized] ?? normalized : normalized;
};
const normalizeOwnershipValue = makeValueMatcher(OWNERSHIP_LABELS);
const normalizePriorityValue = makeValueMatcher(PRIORITY_LABELS);
const normalizeLicensedStatusValue = makeValueMatcher(LICENSED_STATUS_LABELS);

// Backend sometimes returns user-reference objects {uuid, name} for audit fields
function toUserStr(val) {
  if (!val) return null;
  if (typeof val === 'object') return val.name ?? val.email ?? val.uuid ?? null;
  return String(val);
}

// Extract scalar string from either a plain string or an FK object
function toScalar(val, keys = ['slug', 'code', 'value', 'name']) {
  if (!val) return null;
  if (typeof val !== 'object') return String(val);
  for (const k of keys) { if (val[k]) return String(val[k]); }
  return null;
}

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function normalizeWorkflowStatus(value) {
  const text = trimOrNull(value);
  return text ? text.toLowerCase() : null;
}

// ─── Normalize ────────────────────────────────────────────────────────────────
export function normalizeFacility(row = {}) {
  const capacityMetadata = row.capacity_metadata ?? null;
  const metadata = capacityMetadata && typeof capacityMetadata === 'object' && !Array.isArray(capacityMetadata) ? capacityMetadata : null;
  const dataStatus = normalizeWorkflowStatus(
    row.data_status ??
    row.dataStatus ??
    row.validation_status ??
    row.validationStatus ??
    row.workflow_status ??
    row.workflowStatus ??
    'draft'
  ) ?? 'draft';

  return {
    uuid:             row.uuid ?? null,
    id:               row.uuid ?? null,
    name:             row.name ?? '',
    // display labels (for table badges, modals)
    facilityType:     toLabel(FACILITY_TYPE_LABELS, normalizeFacilityTypeValue(row.facility_type)),
    functionalStatus: toLabel(FUNCTIONAL_STATUS_LABELS, normalizeFunctionalStatusValue(row.functional_status)),
    ownership:        toLabel(OWNERSHIP_LABELS, normalizeOwnershipValue(row.ownership)),
    upgradePriority:  toLabel(PRIORITY_LABELS, normalizePriorityValue(row.upgrade_priority)),
    licensedStatus:   toLabel(LICENSED_STATUS_LABELS, normalizeLicensedStatusValue(row.licensed_status)),
    // raw API values — unwrap FK objects if backend returns {uuid,slug,...} instead of scalar
    facility_type:    normalizeFacilityTypeValue(row.facility_type),
    functional_status: normalizeFunctionalStatusValue(row.functional_status),
    ownership_raw:    normalizeOwnershipValue(row.ownership),
    upgrade_priority: normalizePriorityValue(row.upgrade_priority),
    licensed_status:  normalizeLicensedStatusValue(row.licensed_status),
    // geo
    state:            row.state ?? null,
    lga:              row.lga   ?? null,
    ward:             row.ward ?? metadata?.ward ?? null,
    gps_coordinates:  formatGpsCoordinates(
      row.gps_coordinates ??
      row.gpsCoordinates ??
      row.coordinates ??
      row.point_geojson ??
      metadata?.gps_coordinates ??
      metadata?.gpsCoordinates ??
      metadata?.coordinates ??
      null
    ),
    // capacity
    capacity_metadata: capacityMetadata,
    // v2.2 climate sections (stored inside capacity_metadata until the
    // backend has columns for them)
    climate:          metadata?.climate && typeof metadata.climate === 'object' ? metadata.climate : null,
    biogas:           metadata?.biogas && typeof metadata.biogas === 'object' ? metadata.biogas : null,
    waste_recovery:   metadata?.waste_recovery && typeof metadata.waste_recovery === 'object' ? metadata.waste_recovery : null,
    assessment_notes: row.assessment_notes ?? row.notes ?? metadata?.assessment_notes ?? metadata?.notes ?? null,
    notes:            row.notes ?? row.assessment_notes ?? metadata?.assessment_notes ?? metadata?.notes ?? null,
    // workflow
    data_status:      dataStatus,
    dataStatus,
    validationStatus: toLabel(null, dataStatus) ?? 'Draft',
    validation_status: normalizeWorkflowStatus(row.validation_status ?? row.validationStatus ?? row.workflow_status),
    workflow_status:  normalizeWorkflowStatus(row.workflow_status ?? row.workflowStatus),
    workflowStatus:   normalizeWorkflowStatus(row.workflow_status ?? row.workflowStatus),
    submitted_by:     toUserStr(row.submitted_by),
    submitted_at:     row.submitted_at  ?? null,
    validated_by:     toUserStr(row.validated_by),
    validated_at:     row.validated_at  ?? null,
    approved_by:      toUserStr(row.approved_by),
    approved_at:      row.approved_at   ?? null,
    rejected_at:      row.rejected_at   ?? null,
    rejection_notes:  row.rejection_notes ?? row.notes ?? null,
    created_at:       row.created_at ?? null,
    updated_at:       row.updated_at ?? null,
  };
}

// ─── Payload builder ──────────────────────────────────────────────────────────
export function buildFacilityPayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  const dataStatus = normalizeWorkflowStatus(
    scopedPayload.data_status ??
    scopedPayload.dataStatus ??
    scopedPayload.validationStatus ??
    scopedPayload.validation_status ??
    scopedPayload.workflow_status ??
    scopedPayload.workflowStatus
  );
  const metadata = {
    ...((scopedPayload.capacity_metadata && typeof scopedPayload.capacity_metadata === 'object' && !Array.isArray(scopedPayload.capacity_metadata)) ? scopedPayload.capacity_metadata : {}),
  };

  const capacityKeys = [
    'daily_slaughter_capacity',
    'annual_production_capacity',
    'cold_storage_capacity',
    'cold_storage_type',
    'daily_milk_capacity_litres',
    'water_source',
  ];

  for (const key of capacityKeys) {
    if (scopedPayload[key] === undefined || scopedPayload[key] === '') continue;
    metadata[key] = scopedPayload[key];
  }

  const ward = trimOrNull(scopedPayload.ward);
  if (ward !== null) {
    metadata.ward = ward;
  }

  const gpsText = formatGpsCoordinates(scopedPayload.gps_coordinates ?? scopedPayload.gpsCoordinates ?? scopedPayload.coordinates);
  if (gpsText) {
    metadata.gps_coordinates = gpsText;
  }

  const assessmentNotes = trimOrNull(scopedPayload.assessment_notes ?? scopedPayload.notes);
  if (assessmentNotes !== null) {
    metadata.assessment_notes = assessmentNotes;
  }

  const out = {
    state:             trimOrNull(scopedPayload.state),
    lga:               trimOrNull(scopedPayload.lga),
    name:              trimOrNull(scopedPayload.name),
    facility_type:     trimOrNull(normalizeFacilityTypeValue(scopedPayload.facility_type)),
    functional_status: trimOrNull(toBackendFunctionalStatus(scopedPayload.functional_status)),
    ownership:         trimOrNull(normalizeOwnershipValue(scopedPayload.ownership_raw ?? scopedPayload.ownership)),
    licensed_status:   trimOrNull(normalizeLicensedStatusValue(scopedPayload.licensed_status)),
    upgrade_priority:  trimOrNull(normalizePriorityValue(scopedPayload.upgrade_priority)),
    ...(dataStatus ? { data_status: dataStatus } : {}),
  };
  if (Object.keys(metadata).length) out.capacity_metadata = metadata;
  if (scopedPayload.rejection_notes !== undefined || scopedPayload.rejectionNotes !== undefined) {
    out.rejection_notes = trimOrNull(scopedPayload.rejection_notes ?? scopedPayload.rejectionNotes);
  }
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== null));
}

function extractOne(data, targetUuid) {
  const d = data?.data ?? {};
  if (Array.isArray(d.items)) {
    const match = targetUuid ? d.items.find((i) => i.uuid === targetUuid) : d.items[0];
    return normalizeFacility(match ?? {});
  }
  return normalizeFacility(d);
}

// ─── API ──────────────────────────────────────────────────────────────────────
const facilitiesBaseApi = {
  collectionKey: COLLECTION_KEY,

  async list(params = {}) {
    const { data } = await client.get('/v1/facilities', {
      params: applyScopedParams(applyApprovedOnlyParams({ per_page: 25, ...params })),
    });
    const payload = data?.data ?? data ?? {};
    const envelope =
      (Array.isArray(payload?.items) || Array.isArray(payload?.results) || payload?.pagination)
        ? payload
        : payload?.data ?? payload;
    const items = Array.isArray(envelope?.items)
      ? envelope.items
      : Array.isArray(envelope?.results)
        ? envelope.results
        : Array.isArray(envelope)
          ? envelope
          : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.results)
              ? payload.results
              : Array.isArray(payload)
                ? payload
                : [];
    return {
      items: filterItemsByCurrentScope(items.map(normalizeFacility)),
      pagination: envelope?.pagination ?? payload?.pagination ?? {},
    };
  },

  async listAll(params = {}) {
    const fetchPage = async (page) => {
      const { data } = await client.get('/v1/facilities', {
        params: applyScopedParams(applyApprovedOnlyParams({
          per_page: params.per_page ?? 25,
          ...params,
          page,
        })),
      });
      const payload = data?.data ?? data ?? {};
      const envelope =
        (Array.isArray(payload?.items) || Array.isArray(payload?.results) || payload?.pagination)
          ? payload
          : payload?.data ?? payload;
      const items = Array.isArray(envelope?.items)
        ? envelope.items
        : Array.isArray(envelope?.results)
          ? envelope.results
          : Array.isArray(envelope)
            ? envelope
            : Array.isArray(payload?.items)
              ? payload.items
              : Array.isArray(payload?.results)
                ? payload.results
                : Array.isArray(payload)
                  ? payload
                  : [];
      return {
        items: filterItemsByCurrentScope(items.map(normalizeFacility)),
        pagination: envelope?.pagination ?? payload?.pagination ?? {},
      };
    };

    return fetchAllPages(fetchPage, { pageSize: params.per_page ?? 25 });
  },

  // GET /v1/facilities/counts?status=approved
  // Returns a map of facility_type → count, e.g. { abattoir: 2550, veterinary_clinic: 2429, ... }
  async counts(params = {}) {
    try {
      const { data } = await client.get('/v1/facilities/counts', { params: applyScopedParams(applyApprovedOnlyParams(params)) });
      return data?.data ?? data ?? {};
    } catch {
      return {};
    }
  },

  async get(uuid) {
    const { data } = await client.get(`/v1/facilities/${uuid}`);
    return extractOne(data, uuid);
  },

  async create(payload) {
    const { data } = await client.post('/v1/facilities', buildFacilityPayload(payload));
    const item = extractOne(data);
    publishCollectionChange(COLLECTION_KEY, { action: 'create', uuid: item?.uuid ?? null });
    return item;
  },

  async update(uuid, payload) {
    const { data } = await client.put(`/v1/facilities/${uuid}`, buildFacilityPayload(payload));
    const item = extractOne(data, uuid);
    publishCollectionChange(COLLECTION_KEY, { action: 'update', uuid });
    return item;
  },

  async remove(uuid) {
    await client.delete(`/v1/facilities/${uuid}`);
    publishCollectionChange(COLLECTION_KEY, { action: 'remove', uuid });
  },

  async submit(uuid) {
    const { data } = await client.post(`/v1/facilities/${uuid}/submit`);
    const item = extractOne(data, uuid);
    publishCollectionChange(COLLECTION_KEY, { action: 'submit', uuid });
    return item;
  },
  async validate(uuid) {
    const { data } = await client.post(`/v1/facilities/${uuid}/validate`);
    const item = extractOne(data, uuid);
    publishCollectionChange(COLLECTION_KEY, { action: 'validate', uuid });
    return item;
  },
  async approve(uuid) {
    const { data } = await client.post(`/v1/facilities/${uuid}/approve`);
    const item = extractOne(data, uuid);
    publishCollectionChange(COLLECTION_KEY, { action: 'approve', uuid });
    return item;
  },
  async reject(uuid, notes = '') {
    const { data } = await client.post(`/v1/facilities/${uuid}/reject`, { notes });
    const item = extractOne(data, uuid);
    publishCollectionChange(COLLECTION_KEY, { action: 'reject', uuid });
    return item;
  },
};

export const facilitiesApi = withOfflineCollectionSupport(facilitiesBaseApi, {
  basePath: '/v1/facilities',
  collectionKey: COLLECTION_KEY,
  normalize: normalizeFacility,
  buildPayload: buildFacilityPayload,
});

// ─── Option arrays for filter/form dropdowns ──────────────────────────────────
// Each item: { value: '<api_param_value>', label: '<display_text>' }
export const FACILITY_TYPE_OPTIONS     = Object.entries(FACILITY_TYPE_LABELS).map(([v, l])     => ({ value: v, label: l }));
export const FUNCTIONAL_STATUS_OPTIONS = Object.entries(FUNCTIONAL_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }));
export const OWNERSHIP_OPTIONS         = Object.entries(OWNERSHIP_LABELS).map(([v, l])         => ({ value: v, label: l }));
export const UPGRADE_PRIORITY_OPTIONS  = Object.entries(PRIORITY_LABELS).map(([v, l])          => ({ value: v, label: l }));
export const LICENSED_STATUS_OPTIONS   = Object.entries(LICENSED_STATUS_LABELS).map(([v, l])   => ({ value: v, label: l }));

// ─── Lookup helpers (fetch enum options from backend, fall back to statics) ───
export const facilityLookupsApi = {
  // GET /v1/facility-types → [{ uuid, name, slug/code }]
  // Falls back to FACILITY_TYPE_OPTIONS if endpoint not yet live.
  async facilityTypes() {
    try {
      const { data } = await client.get('/v1/facility-types', { params: { per_page: 100 } });
      const raw =
        data?.data?.items ??
        (Array.isArray(data?.data) ? data.data : null) ??
        data?.items ??
        (Array.isArray(data) ? data : null) ??
        [];
      if (!Array.isArray(raw) || raw.length === 0) return FACILITY_TYPE_OPTIONS;
      const mapped = raw
        .map((item) => ({
          value: normalizeFacilityTypeValue(item.slug ?? item.code ?? item.value ?? item.name ?? item.label),
          label: item.name ?? item.label ?? item.display_name,
        }))
        .filter((o) => o.value && o.label);
      if (!mapped.length) return FACILITY_TYPE_OPTIONS;
      // Keep the v2.2 types selectable even if the backend list predates them.
      const known = new Set(mapped.map((o) => o.value));
      const missing = FACILITY_TYPE_OPTIONS.filter((o) => ['livestock_farm', 'biogas_system'].includes(o.value) && !known.has(o.value));
      return [...mapped, ...missing];
    } catch {
      return FACILITY_TYPE_OPTIONS;
    }
  },
};
